import lottie from 'lottie-web';
import { loadFonts } from '@loaders/FontLoader';
import { enablePointerEvents } from '@utils/pointer-events-control';

export default class LoadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'LoadScene' });
  }

  async preload() {
    this.load.json('loading-anim', 'assets/loadAnims/cuemath_loading.json');
    await loadFonts();
  }

  create() {
    this.loadingAnim = this.addLoaderLottie(
      this.cameras.main.width / 2,
      this.cameras.main.height / 2,
    );

    this.assetsLoad();
    this.load.start();

    this.load.on(
      'progress',
      () => {
        this.loadingAnim.anim.playSegments([0, 25], true);
      },
      this,
    );

    this.load.on(
      'complete',
      () => {
        this.loadingAnim.anim.loop = false;
        this.loadingAnim.anim.playSegments([26, 41], true);
        this.loadingAnim.anim.addEventListener('complete', () => {
          enablePointerEvents();
          this.scene.start('StartScene');
        });
      },
      this,
    );
  }

  addLoaderLottie(_x, _y) {
    const lottieRef = this.add
      .dom(_x - this.cameras.main.width / 2, _y - this.cameras.main.height / 2, 'div')
      .setScale(0.7);

    const lottieDiv = lottieRef.node;
    lottieDiv.style.height = '100%';
    lottieDiv.style.width = '100%';

    const lottieAnim = lottie.loadAnimation({
      container: lottieDiv,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      animationData: this.cache.json.get('loading-anim'),
    });

    return { anim: lottieAnim, dom: lottieRef };
  }

  assetsLoad() {
    this.load.multiatlas(
      'spotlight',
      'assets/EndScreenAssets/spotlight/spotlightAnimNew.json',
      'assets/EndScreenAssets/spotlight',
    );
    this.load.multiatlas(
      'endConfetti',
      'assets/EndScreenAssets/confetti/confettiAnimNew.json',
      'assets/EndScreenAssets/confetti',
    );
    this.load.multiatlas(
      'rewardAnim',
      'assets/EndScreenAssets/rewardAnim/rewardAnim.json',
      'assets/EndScreenAssets/rewardAnim',
    );

    this.load.audio('bgaudio', 'assets/Audio/BG music.mp3');
    this.load.audio('confettiAudio', 'assets/Audio/confetti.wav');
    this.load.audio('correctAudio', 'assets/Audio/correct answer.wav');
    this.load.audio('dartHitAudio', 'assets/Audio/Dart throw.wav');
    this.load.audio('onShapeAudio', 'assets/Audio/dart hover.wav');
    this.load.audio('greenLitAudio', 'assets/Audio/Green light.mp3');
    this.load.audio('hintAudio', 'assets/Audio/Hints.mp3');
    this.load.audio('wrongAudio', 'assets/Audio/Wrong answer.mp3');
    this.load.audio('yayAudio', 'assets/Audio/Yay.wav');
    this.load.audio('revealReward', 'assets/Audio/reward reveal.mp3');

    for (let i = 1; i <= 3; i++) {
      this.load.audio('ta' + i, 'assets/Audio/ta' + i + '.mp3');
    }

    this.load.image('mainBG', 'assets/StartSceneAssets/mainBG.png');
    this.load.image('gameName', 'assets/StartSceneAssets/game name.png');
    this.load.image('cartWheelStand', 'assets/StartSceneAssets/cartWheelStand.png');
    this.load.image('parachute', 'assets/StartSceneAssets/parachute.png');
    this.load.image('cloudRight', 'assets/CommonAssets/cloudRighthdpi.png');
    this.load.image('cloudLeft', 'assets/StartSceneAssets/cloudLeft.png');
    this.load.image('playButton', 'assets/StartSceneAssets/Button CTA.png');
    this.load.image('playHover', 'assets/StartSceneAssets/playHover.png');
    this.load.image('ground', 'assets/StartSceneAssets/ground.png');
    this.load.image('cartMask', 'assets/StartSceneAssets/mask.png');

    this.load.image('handIcon', 'assets/TutorialSceneAssets/hand icon.png');
    this.load.image('scene2BG', 'assets/TutorialSceneAssets/scene2BGhdpi.png');
    this.load.image('cartWheel', 'assets/CommonAssets/cartWheelhdpi.png');
    this.load.image('shop', 'assets/TutorialSceneAssets/shopBottomhdpi.png');
    this.load.image('shopTop', 'assets/TutorialSceneAssets/shopTop_1hdpi.png');
    this.load.image('fractionBoard', 'assets/TutorialSceneAssets/fractionBoardhdpi.png');
    this.load.image('nextButton', 'assets/TutorialSceneAssets/nextButton.png');
    this.load.image('nextHover', 'assets/TutorialSceneAssets/NextHover.png');
    this.load.image('hangerLine', 'assets/TutorialSceneAssets/hangerLineNewhdpi.png');
    this.load.image('overlay', 'assets/TutorialSceneAssets/overlay.png');
    for (let i = 1; i <= 4; i++) {
      this.load.image('shape' + i, 'assets/TutorialSceneAssets/shape' + i + '.png');
    }
    for (let i = 1; i <= 3; i++) {
      this.load.image('tutSceneText' + i, 'assets/TutorialSceneAssets/tutSceneText' + i + 'hdpi.png');
    }
    this.load.atlas(
      'confetti',
      'assets/TutorialSceneAssets/confettispritesheet.png',
      'assets/TutorialSceneAssets/confettispritesheet.json',
    );
    this.load.image('mask', 'assets/TutorialSceneAssets/maskhdpi.png');
    this.load.image('fraction', 'assets/TutorialSceneAssets/fractionTexthdpi.png');

    for (let i = 1; i < 11; i++) {
      for (let j = 1; j < 5; j++) {
        this.load.image('question_' + i + '_' + j, 'assets/question_' + i + '/' + '0' + j + '.png');
      }
    }
    this.load.atlas('dartHit_1', 'assets/TutorialSceneAssets/dart1hit.png', 'assets/TutorialSceneAssets/dart1hit.json');
    this.load.atlas('dartHit_2', 'assets/TutorialSceneAssets/dart2hit.png', 'assets/TutorialSceneAssets/dart2hit.json');
    this.load.atlas('dartHit_3', 'assets/TutorialSceneAssets/dart3hit.png', 'assets/TutorialSceneAssets/dart3hit.json');
    this.load.atlas('dartHit_4', 'assets/TutorialSceneAssets/dart4hit.png', 'assets/TutorialSceneAssets/dart4hit.json');

    this.load.atlas('dartMiss_1', 'assets/TutorialSceneAssets/dart1miss.png', 'assets/TutorialSceneAssets/dart1miss.json');
    this.load.atlas('dartMiss_2', 'assets/TutorialSceneAssets/dart2miss.png', 'assets/TutorialSceneAssets/dart2miss.json');
    this.load.atlas('dartMiss_3', 'assets/TutorialSceneAssets/dart3miss.png', 'assets/TutorialSceneAssets/dart3miss.json');
    this.load.atlas('dartMiss_4', 'assets/TutorialSceneAssets/dart4miss.png', 'assets/TutorialSceneAssets/dart4miss.json');

    for (let i = 1; i <= 10; i++) {
      this.load.image('fraction_' + i, 'assets/question_' + i + '/' + 'fraction' + i + '.png');
    }

    this.load.image('correctBulb', 'assets/TutorialSceneAssets/correctBulb.png');
    this.load.image('hintIcon', 'assets/TutorialSceneAssets/Hint.png');
    this.load.image('hint_1', 'assets/TutorialSceneAssets/hint-1.png');
    this.load.image('hint_2', 'assets/TutorialSceneAssets/hint-2.png');
    this.load.image('closeHint', 'assets/TutorialSceneAssets/closeHintIconhdpi.png');

    this.load.atlas(
      'hintFinalAnim',
      'assets/TutorialSceneAssets/hintFinalAnim.png',
      'assets/TutorialSceneAssets/hintFinalAnim.json',
    );
    this.load.atlas(
      'flagWave',
      'assets/TutorialSceneAssets/flagWaveAnim.png',
      'assets/TutorialSceneAssets/flagWaveAnim.json',
    );
  }

  update() {}
}
